/**
 * iter 180 - Platform-wide timezone formatting for Mundalo.
 *
 * The database always stores UTC. This helper is the single source of
 * truth for RENDERING absolute dates/times in the founder's local zone
 * (Dallas / US Central). Relative displays ("2h ago") don't route
 * through here and don't need to.
 *
 * Usage:
 *   formatCentralDate(iso)                     // "Nov 12, 2025"
 *   formatCentralDateTime(iso)                 // "Nov 12, 2025, 3:42 PM"
 *   formatCentralDate(iso, { year: undefined }) // "Nov 12"
 */

export const CENTRAL_TZ = 'America/Chicago';

/** Default date-only options. */
const DATE_OPTS = { month: 'short', day: 'numeric', year: 'numeric' };
/** Default datetime options - matches the majority of admin log rows. */
const DATETIME_OPTS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const _safe = (iso) => {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

export function formatCentralDate(iso, opts = {}) {
  const d = _safe(iso);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', { ...DATE_OPTS, ...opts, timeZone: CENTRAL_TZ });
}

export function formatCentralDateTime(iso, opts = {}) {
  const d = _safe(iso);
  if (!d) return '-';
  return d.toLocaleString('en-US', { ...DATETIME_OPTS, ...opts, timeZone: CENTRAL_TZ });
}

export function formatCentralTime(iso, opts = {}) {
  const d = _safe(iso);
  if (!d) return '-';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...opts,
    timeZone: CENTRAL_TZ,
  });
}

/**
 * "Nov 12, 2025 · 3:42 PM CT" - full timestamp with an explicit CT tag.
 * Used in tooltips and audit rows where the timezone must be unambiguous.
 */
export function formatCentralFull(iso) {
  const d = _safe(iso);
  if (!d) return '-';
  const date = d.toLocaleDateString('en-US', { ...DATE_OPTS, timeZone: CENTRAL_TZ });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: CENTRAL_TZ,
  });
  return `${date} · ${time} CT`;
}
